import React from 'react'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './card'
import { useThemeStyle } from '@/components/theme/ThemeProvider'
import { cn } from '@/lib/utils'

interface ThemedCardProps {
  children: React.ReactNode
  className?: string
  variant?: 'default' | 'elevated' | 'bordered' | 'minimal'
  hover?: boolean
  onClick?: () => void
}

export function ThemedCard({ children, className, variant = 'default', hover = true, onClick }: ThemedCardProps) {
  const { theme } = useThemeStyle()

  const getVariantStyles = () => {
    const baseStyles = {
      padding: theme.components.card.padding,
      borderRadius: theme.styles.borderRadius.lg,
      transition: `all ${theme.styles.animation.duration} ${theme.styles.animation.easing}`
    }

    switch (theme.components.card.variant) {
      case 'shadow':
        return {
          ...baseStyles,
          boxShadow: theme.styles.effects.shadow,
          border: 'none'
        }
      case 'bordered':
        return {
          ...baseStyles,
          boxShadow: 'none',
          border: '1px solid hsl(var(--border))'
        }
      case 'minimal':
        return {
          ...baseStyles,
          boxShadow: 'none',
          border: 'none',
          backgroundColor: 'transparent'
        }
      default:
        return {
          ...baseStyles,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid hsl(var(--border))'
        }
    }
  }

  const hoverStyles = hover ? {
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.styles.effects.shadow
    }
  } : {}

  return (
    <Card
      className={cn(
        'transition-all duration-[--theme-duration] ease-[--theme-easing]',
        hover && 'hover:scale-[1.02] hover:-translate-y-1',
        onClick && 'cursor-pointer',
        className
      )}
      style={{
        ...getVariantStyles(),
        ...hoverStyles
      }}
      onClick={onClick}
    >
      {children}
    </Card>
  )
}

// Componentes específicos para diferentes tipos de conteúdo
interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  description: string
  className?: string
}

export function FeatureCard({ icon, title, description, className }: FeatureCardProps) {
  const { theme } = useThemeStyle()

  return (
    <ThemedCard className={cn('text-center', className)}>
      <CardContent 
        className="pt-6"
        style={{ padding: theme.components.card.padding }}
      >
        <div className="mb-4 flex justify-center text-primary">
          {icon}
        </div>
        <h3 
          className="text-xl mb-2 text-foreground"
          style={{ fontWeight: theme.styles.typography.headingWeight }}
        >
          {title}
        </h3>
        <p className="text-muted-foreground">
          {description}
        </p>
      </CardContent>
    </ThemedCard>
  )
}

interface StatCardProps {
  number: string
  label: string
  trend?: 'up' | 'down' | 'stable'
  className?: string
  onClick?: () => void
}

export function StatCard({ number, label, trend, className, onClick }: StatCardProps) {
  const { theme } = useThemeStyle()

  const trendColors = {
    up: 'text-green-600',
    down: 'text-red-600',
    stable: 'text-muted-foreground'
  }

  return (
    <ThemedCard className={cn('text-center', className)} onClick={onClick}>
      <CardContent 
        style={{ padding: theme.components.card.padding }}
      >
        <div 
          className="text-3xl font-bold text-foreground mb-1"
          style={{ 
            fontWeight: theme.styles.typography.headingWeight,
            fontSize: '2rem'
          }}
        >
          {number}
        </div>
        <div className={cn('text-sm', trend && trendColors[trend])}>
          {label}
        </div>
      </CardContent>
    </ThemedCard>
  )
}